import { useProfileData } from '../hooks/useProfileData';
import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import {
  Avatar,
  Box,
  Typography,
  IconButton,
  Divider,
  Stack,
  Button,
  Modal,
  Backdrop,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import DefaultUserImage from "../assets/images/default-avatar.png"
import { useAuth } from '../hooks/useAuth';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ProfileIcon from '../svg/Profilenavicons/ProfileIcon';
import ProductCard from '../views/Products/ProductCard';
import { useProductData } from '../hooks/useProductData';
import WhatsAppButton from './WhatsAppButton';

interface PremiumCreatorInfoProps {
  anchorEl: any;
  requiredPremiumKey: string;
  premiumAccess: boolean;
  setShowLogin: React.Dispatch<React.SetStateAction<boolean>>;
  isProductChatPage?: boolean
}

const FrankSortinoNav = lazy(
  () => import('../views/customUIElements/FrankSortino')
);

const PremiumCreatorInfo: React.FC<PremiumCreatorInfoProps> = (props) => {
  const { anchorEl, requiredPremiumKey, premiumAccess, setShowLogin, isProductChatPage = false } = props;
  const { creatorProfileData } = useProfileData();
  const { productInfo, incrementClickCount } = useProductData();
  const { authUser, isAnonymousUser, setLoginModalVisible } = useAuth();
  const [showBoughtModal, setShowBoughtModal] = useState(false);
  
  const navigate = useNavigate();
  const theme = useTheme();
  
  const handleProductClick = () => {
    // Increment local click count (which now handles logging)
    incrementClickCount(creatorProfileData?.userId, productInfo?.id);
    
    // Open product URL in new tab
    if (productInfo?.productURL) {
      window.open(productInfo.productURL, '_blank');
    }
  }

  if (!creatorProfileData) {
    return <></>;
  }

  const getRoomId = () => {
    if (authUser?.documentId) {
      if (isProductChatPage && productInfo) {
        return `${creatorProfileData?.userId}.${authUser?.documentId}.product_${productInfo?.id}`;
      }
      return `${creatorProfileData?.userId}.${authUser?.documentId}`;
    } else {
      if (isProductChatPage && productInfo) {
        return `${creatorProfileData?.userId}.anonymous-user.product_${productInfo?.id}`;
      }
      return `${creatorProfileData?.userId}.anonymous-user`;
    }
  };

  const roomId = getRoomId();

  if (
    creatorProfileData.userName === 'srinivasjaini' ||
    creatorProfileData.userName === 'sortino-chat'
  ) {
    return (
      <Suspense>
        <FrankSortinoNav
          anchorEl={anchorEl}
          featureKey={requiredPremiumKey}
          hasAccess={premiumAccess}
          setShowLogin={setShowLogin}
        />
      </Suspense>
    );
  }

  return (
    <>
      {/* Mobile Save Button */}
      {!creatorProfileData.email && !authUser && (
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            width: '100%',
            p: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper'
          }}
        >
          <Button
            variant='contained'
            startIcon={<SaveAltIcon />}
            fullWidth
            onClick={() =>
              setLoginModalVisible({
                isOpen: true,
                formType: 'signup',
                currentStep: 1
              })
            }
            style={{ backgroundColor: '#0810FF', color: '#fff' }}
          >
            Save my persona
          </Button>
        </Box>
      )}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: { xs: 'row', md: 'column' },
          justifyContent: { xs: 'center', md: 'flex-start' },
          alignItems: { xs: 'center', md: 'start' },
          width: '100%',
          height: { xs: '66px', md: '100%' },
          flexGrow: 1,
          boxShadow: `0 4px 4px ${theme.palette.primary.main}33`,
          maxHeight: '100%',
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            width: { xs: '40px', md: '27px' },
            height: { xs: '40px', md: '27px' },
            backgroundColor: (theme) => theme.palette.secondary.light,
            borderRadius: { xs: '16px', md: '11px' },
            mt: 3,
            mx: 2.5,
          }}
        >
          <ArrowBackIcon
            sx={{
              width: { xs: '22px', md: '18px' },
              height: { xs: '22px', md: '18px' },
            }}
          />
        </IconButton>

        <Box
          width={'100%'}
          justifyContent={'cflex-start'}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            width: '100%',
            maxHeight: '100%',
            overflowY: 'auto',
            pb: 3,
          }}
        >
          <Avatar
            src={creatorProfileData?.profilePicUrl}
            alt={creatorProfileData?.fullName}
            component={isAnonymousUser ? 'div' : 'a'}
            href={isAnonymousUser ? '' : `/${creatorProfileData.userName}`}
            sx={{
              mx: 2.5,
              width: { xs: '40px', md: 'auto' },
              height: { xs: '40px', md: 'auto' },
              fontSize: '1.25rem',
              aspectRatio: 1,
              borderRadius: '40%',
              cursor: isAnonymousUser ? 'default' : 'pointer',
              color: 'primary.main',
              textDecoration: 'none',
            }}
            onClick={!isAnonymousUser ? (e) => {
              e.stopPropagation();
              e.preventDefault();
              navigate(`/${creatorProfileData.userName}`, { replace: true })
            } : undefined}
          >
            {!creatorProfileData?.profilePicUrl &&
              (isAnonymousUser) && (
                <Box sx={{ bgcolor: (theme) => theme.palette.background['paper-elevation-8'], width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <ProfileIcon darkMode={theme.palette.mode === 'dark'} style={{ width: 60, height: 60 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.primary',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setLoginModalVisible({
                        isOpen: true,
                        formType: 'signup',
                        currentStep: 1,
                      });
                    }
                    }
                  >
                    + Add Avatar
                  </Typography>
                </Box>
              )}
            {(
              creatorProfileData?.profilePicUrl ||
              !(isAnonymousUser || creatorProfileData.userId === authUser?.uid)) &&
              creatorProfileData?.fullName ? creatorProfileData?.fullName[0] : ''}
          </Avatar>
          <Stack
            direction='column'
            mt={{ xs: 0, md: 3 }}
            mx={2.5}
            justifyContent={{ xs: 'center', md: 'flex-start' }}
          >
            <Box display='flex' alignItems='center'>
              <Typography variant='h7' color='primary' align='left'>
                {creatorProfileData?.fullName}
              </Typography>
              {/* <VerifiedIcon
              sx={{ ml: 0.5, fill: '#3EACEF', width: '18px', height: '18px' }}
            /> */}
            </Box>
            <Typography
              variant='body2'
              color='textSecondary'
              align='left'
              sx={{ m: 0, p: 0, lineHeight: 1.25 }}
            >
              @{creatorProfileData?.userName}
            </Typography>
          </Stack>

          <Divider
            sx={{
              display: { xs: 'none', md: 'block' },
              width: '100%',
              height: '0.5px',
              backgroundColor: 'text.primary',
              opacity: 0.1,
              my: 1,
            }}
          />

          <Typography
            variant='body2'
            color='textPrimary'
            align={'left'}
            display={{ xs: 'none', md: 'block' }}
            sx={{
              px: 2.5,
            }}
          >
            {creatorProfileData?.shortBio}
          </Typography>

          {!creatorProfileData.email && !authUser && (
            <Box mx={2.5} mt={2}>
              <Button
                variant='contained'
                startIcon={<SaveAltIcon />}
                fullWidth
                onClick={() =>
                  setLoginModalVisible(prev => ({
                    ...prev,
                    isOpen: true,
                    formType: 'signup',
                    currentStep: 1,
                  }))
                }
                style={{ backgroundColor: '#0810FF', color: '#fff' }}
              >
                Save AI persona
              </Button>
            </Box>
          )}
          {isProductChatPage &&
            <Box sx={{
              p: 1,
              cursor: 'pointer',

            }} onClick={handleProductClick}>
              <ProductCard product={productInfo} wrap />
            </Box>
          }
        </Box>
        <WhatsAppButton variant='desktop' roomId={roomId} />
        <Modal
          open={showBoughtModal}
          onClose={() => setShowBoughtModal(false)}
          slots={{ backdrop: Backdrop }}
          slotProps={{ backdrop: { timeout: 500 } }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              margin: 'auto',
              maxWidth: '500px',
              minWidth: '400px',
              bgcolor: 'background.paper',
              boxShadow: 24,
              borderRadius: '10px',
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Typography variant='h6'>Did you buy the product?</Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                width: 1,
              }}
            >
              <Button
                variant='contained'
                sx={{
                  width: 1
                }}
                onClick={() => {
                  incrementClickCount(creatorProfileData?.userId, productInfo?.id)
                  setShowBoughtModal(false)
                }}
              >Yes</Button>
              <Button
                variant='contained'
                color='error'
                sx={{
                  width: 1
                }}

                onClick={() => setShowBoughtModal(false)}
              >No</Button>
            </Box>
          </Box>
        </Modal>
      </Box>
    </>
  );
};

export default PremiumCreatorInfo;
